// src/pages/Accounting/Receipts/index.tsx

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { Receipt } from "./types";
import { PALETTE } from "./constants";
import { EmptyState } from "./components/EmptyState";
import { Dashboard } from "./components/Dashboard";
import { ViewDrawer } from "./components/ViewDrawer";
import { ReviewMode } from "./components/ReviewMode";
import type { ReviewDraft } from "./components/ReviewMode/ReviewForm";
import { mockReceiptsEmpty, mockReceiptsPopulated } from "./mockData";

export function ReceiptsHeader() {
  const navigate = useNavigate();
  return (
    <div style={headerStyles.wrap}>
      <div style={headerStyles.crumb}>
        <span
          style={headerStyles.crumbLink}
          onClick={() => navigate("/accounting")}
        >
          Accounting
        </span>
        <span style={headerStyles.crumbSep}>/</span>
        <span>Receipts</span>
      </div>
      <h1 style={headerStyles.title}>Receipts</h1>
      <div style={headerStyles.sub}>
        Snap or forward every receipt. It gets matched to the right bank
        transaction and tucked away for tax time.
      </div>
    </div>
  );
}

const headerStyles: Record<string, React.CSSProperties> = {
  wrap: { padding: "14px 32px 14px" },
  crumb: {
    fontSize: 13,
    color: PALETTE.ink3,
    marginBottom: 4,
    fontWeight: 500,
  },
  crumbLink: { color: PALETTE.ink3, cursor: "pointer" },
  crumbSep: { color: PALETTE.ink3, margin: "0 6px" },
  title: {
    fontSize: 32,
    fontWeight: 800,
    margin: 0,
    letterSpacing: "-0.02em",
    color: PALETTE.ink,
  },
  sub: {
    fontSize: 13.5,
    color: PALETTE.ink3,
    marginTop: 6,
    fontWeight: 500,
  },
};

function getInitialReceipts(): Receipt[] {
  if (typeof window === "undefined") return mockReceiptsEmpty;
  const params = new URLSearchParams(window.location.search);
  if (params.get("demo") === "full") return mockReceiptsPopulated;
  return mockReceiptsEmpty;
}

function useUserEmail(): string {
  return "you@brightcare.com";
}

export default function ReceiptsPage() {
  const userEmail = useUserEmail();
  const [receipts, setReceipts] = useState<Receipt[]>(getInitialReceipts());
  const [selectedReceiptId, setSelectedReceiptId] = useState<string | null>(
    null,
  );
  const [reviewQueue, setReviewQueue] = useState<Receipt[]>([]);
  const [reviewOpen, setReviewOpen] = useState(false);

  const selectedReceipt = useMemo(
    () => receipts.find((r) => r.id === selectedReceiptId) ?? null,
    [receipts, selectedReceiptId],
  );

  const handleFilesDropped = (files: File[]) => {
    console.log("Files dropped:", files.map((f) => f.name));
    setReceipts(mockReceiptsPopulated);
  };
  const handleEnterManually = () => console.log("Enter manually clicked");
  const handleGetMobileApp = () => console.log("Get mobile app clicked");
  const handleConnectInbox = () => console.log("Connect inbox clicked");

  const handleRowClick = (id: string) => setSelectedReceiptId(id);
  const handleCloseDrawer = () => setSelectedReceiptId(null);
  const handleEditDetails = () =>
    console.log("Edit details clicked:", selectedReceiptId);

  const handleReviewClick = () => {
    setReviewQueue(
      receipts.filter((r) => r.matchStatus === "needs-review"),
    );
    setReviewOpen(true);
  };
  const handleCloseReview = () => setReviewOpen(false);

  const handleUnmatch = () => {
    if (!selectedReceiptId) return;
    setReceipts((prev) =>
      prev.map((r) =>
        r.id === selectedReceiptId
          ? {
              ...r,
              matchStatus: "unmatched",
              matchedTransactionId: undefined,
              matchedTransaction: undefined,
            }
          : r,
      ),
    );
    setSelectedReceiptId(null);
  };

  const handleDelete = () => {
    if (!selectedReceiptId) return;
    setReceipts((prev) => prev.filter((r) => r.id !== selectedReceiptId));
    setSelectedReceiptId(null);
  };

  const handleOpenInBankTransactions = () => {
    const txId = selectedReceipt?.matchedTransaction?.id;
    console.log("Open transaction in Bank transactions:", txId);
  };

  const handleApproveReceipt = (
    receiptId: string,
    draft: ReviewDraft,
    keepMatch: boolean,
  ) => {
    setReceipts((prev) =>
      prev.map((r) =>
        r.id === receiptId
          ? {
              ...r,
              vendorName: draft.vendorName,
              date: draft.date,
              total: draft.total,
              tax: draft.tax,
              category: draft.category || undefined,
              notes: draft.notes || undefined,
              matchStatus:
                keepMatch && r.matchedTransaction ? "matched" : "unmatched",
              matchedTransaction: keepMatch ? r.matchedTransaction : undefined,
              matchedTransactionId: keepMatch
                ? r.matchedTransactionId
                : undefined,
            }
          : r,
      ),
    );
  };

  const handleRejectReceipt = (receiptId: string) => {
    setReceipts((prev) => prev.filter((r) => r.id !== receiptId));
  };

  const handleSkipReceipt = (receiptId: string) => {
    console.log("Skipped:", receiptId);
  };

  const isEmpty = receipts.length === 0;

  return (
    <div
      style={{
        maxWidth: "100%",
        margin: 0,
        marginTop: -28,
        padding: "20px 32px 90px",
        background: "#fff",
        minHeight: "100%",
      }}
    >
      {isEmpty ? (
        <EmptyState
          userEmail={userEmail}
          onFilesDropped={handleFilesDropped}
          onEnterManually={handleEnterManually}
          onGetMobileApp={handleGetMobileApp}
          onConnectInbox={handleConnectInbox}
        />
      ) : (
        <Dashboard
          receipts={receipts}
          onRowClick={handleRowClick}
          onReviewClick={handleReviewClick}
        />
      )}

      {selectedReceipt && (
        <ViewDrawer
          receipt={selectedReceipt}
          onClose={handleCloseDrawer}
          onEdit={handleEditDetails}
          onUnmatch={handleUnmatch}
          onDelete={handleDelete}
          onOpenInBankTransactions={handleOpenInBankTransactions}
        />
      )}

      {reviewOpen && (
        <ReviewMode
          receipts={reviewQueue}
          onApprove={handleApproveReceipt}
          onReject={handleRejectReceipt}
          onSkip={handleSkipReceipt}
          onClose={handleCloseReview}
        />
      )}
    </div>
  );
}